import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { Auth0Provider } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import 'core-js/stable'
import 'react-loading-skeleton/dist/skeleton.css'
import 'regenerator-runtime/runtime'
import { useAuthStore } from '@/stores/authStore'
import { handleServerError } from '@/utils/handle-server-error'
import { toast } from '@/hooks/use-toast'
import { AuthWrapper } from './auth'
import { AccessProvider } from './components/access-guards'
import { FaviconHandler } from './components/favicon-handler'
import { PendingInviteChecker } from './components/layout/pending-invite-modal'
import { ThemeProvider } from './context/theme-context'
import { SecurityWrapper } from './hooks/use-OrgValidator'
import './index.css'
// Generated Routes
import { routeTree } from './routeTree.gen'

const apiUrl = import.meta.env.VITE_API_URL

Sentry.init({
  dsn: 'https://6e1940d0d84f02f477d181386bf7a687@o4508207776464896.ingest.us.sentry.io/4508654733623298',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  tracePropagationTargets: ['localhost', apiUrl],
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% sample rate for sessions
  replaysOnErrorSampleRate: 1.0, // 100% sample rate for sessions with errors
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log({ failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
    },
    mutations: {
      onError: (error) => {
        handleServerError(error)

        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast({
              variant: 'destructive',
              title: 'Content not modified!',
            })
          }
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          toast({
            variant: 'destructive',
            title: 'Session expired!',
          })
          useAuthStore.getState().auth.reset()
          const redirect = `${router.history.location.href}`
          router.navigate({ to: '/sign-in', search: { redirect } })
        }
        if (error.response?.status === 500) {
          toast({
            variant: 'destructive',
            title: 'Internal Server Error!',
          })
          router.navigate({ to: '/500' })
        }
        if (error.response?.status === 403) {
          // router.navigate("/forbidden", { replace: true });
        }
      }
    },
  }),
})

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Add this new component before root.render()

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <Auth0Provider
        domain='dev-qm8j24n3k3oqvr3x.us.auth0.com' // Replace with your Auth0 domain
        clientId='jhMLnWHAzW6ImPfMzFFNsdRhv1gErjiq' // Replace with your Auth0 client ID
        authorizationParams={{
          redirect_uri: `${window.location.origin}/callback`,
          response_type: 'code',
          audience: 'https://example.com', // Your API identifier
          scope: 'openid profile email',
          useRefreshTokens: true,
          cacheLocation: 'localstorage', // Persist authentication state
        }}
        onRedirectCallback={(appState) => {
          router.navigate({
            to: appState?.returnTo || '/',
            search: appState?.search,
          })
        }}
      >
        <FaviconHandler />

        <AccessProvider>
          <AuthWrapper>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider defaultTheme='light' storageKey='vite-ui-theme'>
                <SecurityWrapper>
                  <PendingInviteChecker />
                  <RouterProvider router={router} />
                </SecurityWrapper>
              </ThemeProvider>
            </QueryClientProvider>
          </AuthWrapper>
        </AccessProvider>
      </Auth0Provider>
    </StrictMode>
  )
}
